import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Wand2, Sparkles, Lightbulb, X } from "lucide-react";

export default function CaseIntake() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [statementText, setStatementText] = useState("");
  const [playbooks, setPlaybooks] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [tacticsPanel, setTacticsPanel] = useState(null);
  const [form, setForm] = useState({
    merchant_name: "",
    subscription_id: "",
    amount: "",
    charge_date: "",
    cancel_date: "",
    account_email: user?.email || "",
    account_name: user?.name || "",
    issue_details: "",
    reason: "charged_after_cancel",
  });

  useEffect(() => { api.get("/playbooks").then((r) => setPlaybooks(r.data)).catch(()=>{}); }, []);

  const suggestions = useMemo(() => {
    const needle = form.merchant_name.trim().toLowerCase();
    if (!needle || needle.length < 2) return [];
    return playbooks.filter((p) => p.name.toLowerCase().includes(needle) || p.category.toLowerCase().includes(needle)).slice(0, 5);
  }, [form.merchant_name, playbooks]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pickSuggestion = (p) => {
    setForm({ ...form, merchant_name: p.name });
    setShowSuggest(false);
    setTacticsPanel(p);
  };

  const autofill = async () => {
    if (!statementText.trim()) {
      toast.error("Paste a charge line first");
      return;
    }
    setAutoBusy(true);
    try {
      const r = await api.post("/parse-charge", { text: statementText });
      const filled = {};
      if (r.data.merchant_name) filled.merchant_name = r.data.merchant_name;
      if (r.data.amount) filled.amount = String(r.data.amount);
      if (r.data.charge_date) filled.charge_date = r.data.charge_date;
      if (r.data.subscription_id) filled.subscription_id = r.data.subscription_id;
      if (Object.keys(filled).length === 0) {
        toast.error("Couldn't auto-detect — please fill the form manually");
      } else {
        setForm({ ...form, ...filled });
        toast.success(`Auto-filled ${Object.keys(filled).length} field${Object.keys(filled).length === 1 ? "" : "s"}. Review before submitting.`);
      }
    } catch (e) {
      toast.error("Auto-detect failed — try filling manually");
    } finally {
      setAutoBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      const r = await api.post("/cases", payload);
      toast.success("Case created");
      nav(`/case/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create case");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFBFD]">
      <Nav />
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Tell us about the charge</h1>
        <p className="text-slate-600 mt-2">90 seconds. We keep this simple.</p>

        <Card className="trust-card mt-6 border-[#BFDBFE] bg-[#EEF4FF]/40" data-testid="auto-detect-card">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-[#1E3A8A]" />
              <p className="font-semibold text-slate-900 text-sm">Skip typing — paste your charge line</p>
            </div>
            <p className="text-xs text-slate-600 mb-3">Copy the row from your bank/credit-card statement and we'll auto-fill the merchant, amount, and date.</p>
            <Textarea
              data-testid="auto-detect-input"
              rows={2}
              value={statementText}
              onChange={(e) => setStatementText(e.target.value)}
              placeholder="e.g., 02/10/2026  NETFLIX.COM  $15.49  CA"
              className="bg-white"
            />
            <Button
              type="button"
              onClick={autofill}
              disabled={autoBusy}
              data-testid="auto-detect-button"
              className="mt-3 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-10 px-4"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              {autoBusy ? "Reading…" : "Auto-fill form"}
            </Button>
          </CardContent>
        </Card>

        <Card className="trust-card mt-6">
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={submit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="relative">
                  <Label>Merchant name</Label>
                  <Input
                    data-testid="case-intake-merchant-name"
                    required
                    value={form.merchant_name}
                    onChange={(e) => { set("merchant_name")(e); setShowSuggest(true); }}
                    onFocus={() => setShowSuggest(true)}
                    onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
                    placeholder="Netflix"
                    autoComplete="off"
                  />
                  {showSuggest && suggestions.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden" data-testid="merchant-suggestions">
                      {suggestions.map((p) => (
                        <button
                          type="button"
                          key={p.slug}
                          onMouseDown={(e) => { e.preventDefault(); pickSuggestion(p); }}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-2"
                          data-testid={`merchant-suggestion-${p.slug}`}
                        >
                          <div>
                            <p className="text-sm font-medium text-slate-900">{p.name}</p>
                            <p className="text-xs text-slate-500">{p.category} · response {p.typical_response_days} days</p>
                          </div>
                          <Badge variant="outline" className="rounded-full text-[10px] bg-blue-50 text-blue-700 border-blue-200">Playbook</Badge>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Subscription / Account ID</Label>
                  <Input data-testid="case-intake-subscription-id" value={form.subscription_id} onChange={set("subscription_id")} placeholder="Optional — helpful if you have it" />
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label>Amount charged (USD)</Label>
                  <Input data-testid="case-intake-amount-charged" required type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} placeholder="15.49" />
                </div>
                <div>
                  <Label>Charge date</Label>
                  <Input data-testid="case-intake-charge-date" required type="date" value={form.charge_date} onChange={set("charge_date")} />
                </div>
                <div>
                  <Label>Cancel date</Label>
                  <Input data-testid="case-intake-cancel-date" required type="date" value={form.cancel_date} onChange={set("cancel_date")} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Your name (on account)</Label>
                  <Input data-testid="case-intake-account-name" required value={form.account_name} onChange={set("account_name")} placeholder="Alex Rivera" />
                </div>
                <div>
                  <Label>Account email</Label>
                  <Input data-testid="case-intake-account-email" required type="email" value={form.account_email} onChange={set("account_email")} placeholder="alex@example.com" />
                </div>
              </div>
              <div>
                <Label>What happened? (optional)</Label>
                <Textarea data-testid="case-intake-details" rows={3} value={form.issue_details} onChange={set("issue_details")} placeholder="Cancelled through the app on the 10th, but was charged on the 15th. No refund email came through." />
                <p className="text-xs text-slate-500 mt-1">Just facts. Our AI paraphrases this into a calm 1-2 sentence summary.</p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button data-testid="case-intake-submit-button" type="submit" disabled={busy} className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-12 px-6">
                  {busy ? "Creating…" : "Create case → see messages"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        <p className="text-xs text-slate-500 mt-4">You'll be asked to pay $19 (non-refundable) to unlock your 4 messages. No autopay, no subscription.</p>

        {tacticsPanel && (
          <div className="fixed inset-y-0 right-0 w-full sm:max-w-md bg-white shadow-2xl border-l border-slate-200 z-40 overflow-y-auto" data-testid="tactics-side-panel">
            <div className="p-6 sticky top-0 bg-white border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                <h3 className="font-semibold text-slate-900">{tacticsPanel.name} tactics</h3>
              </div>
              <button onClick={() => setTacticsPanel(null)} className="text-slate-400 hover:text-slate-900" data-testid="tactics-panel-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <Badge variant="outline" className="rounded-full text-xs bg-blue-50 text-blue-700 border-blue-200">Typical response: {tacticsPanel.typical_response_days} days</Badge>
              <ul className="mt-4 space-y-3">
                {tacticsPanel.tips.map((t, i) => (
                  <li key={i} className="flex gap-2 text-sm text-slate-700">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-[#EEF4FF] text-[#1E3A8A] text-[10px] font-bold grid place-items-center mt-0.5">{i+1}</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              {tacticsPanel.support_url && (
                <a href={tacticsPanel.support_url} target="_blank" rel="noreferrer" className="mt-6 inline-block text-sm text-[#1E3A8A] hover:underline">Open {tacticsPanel.name} support →</a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
